import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/auth';
import {
  LoginRequest,
  LoginResponse,
  VerifyOTPRequest,
  VerifyOTPResponse,
  ResendOTPRequest,
  ResendOTPResponse,
  AcceptInviteRequest,
  AcceptInviteResponse,
} from '@/types/api';
import { ApiError, apiClient } from '@/lib/api-client';
import { logAuthStage } from '@/lib/auth-log';
import { tokenStorage } from '@/utils/token-storage';
import { authUtils } from '@/utils/auth';

// Signup mutation hook
export const useSignup = () => {
  return useMutation({
    mutationFn: authApi.signup,
    onSuccess: () => {
      logAuthStage('signup-success');
    },
    onError: (error) => {
      console.error('Signup failed:', error);
    },
  });
};

// Login mutation hook
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<LoginResponse, ApiError, LoginRequest>({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      logAuthStage('login-success', { hasToken: Boolean(data.access_token) });
      // Clear all cached data to ensure fresh data for the new user
      queryClient.clear();

      // Store both access and refresh tokens on successful login
      if (data.access_token && data.refresh_token) {
        tokenStorage.setTokens(data.access_token, data.refresh_token);
      } else if (data.access_token) {
        tokenStorage.setToken(data.access_token);
      }
      if (data.user) {
        authUtils.setUserData(data.user);
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

// Logout mutation hook
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: authApi.logout,
    onSuccess: () => {
      logAuthStage('logout-success');
      // Clear all cached data on logout
      queryClient.clear();
      // Clear all stored data on successful logout
      authUtils.logout();
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Clear cached data and stored data even if logout API fails
      queryClient.clear();
      authUtils.logout();
    },
  });
};

// OTP Verification mutation hook
export const useVerifyOTP = () => {
  return useMutation<VerifyOTPResponse, ApiError, VerifyOTPRequest>({
    mutationFn: authApi.verifyOTP,
    onSuccess: (data) => {
      logAuthStage('otp-success', { hasToken: Boolean(data.access_token) });
      // Store tokens exactly like useLogin so subsequent protected calls (profile update, logo upload) succeed
      if (data.access_token && data.refresh_token) {
        tokenStorage.setTokens(data.access_token, data.refresh_token);
      }
    },
    onError: (error) => {
      console.error('OTP verification failed:', error);
    },
  });
};

// Resend OTP mutation hook
export const useResendOTP = () => {
  return useMutation<ResendOTPResponse, ApiError, ResendOTPRequest>({
    mutationFn: authApi.resendOTP,
    onSuccess: () => {
      logAuthStage('otp-resend-success');
    },
    onError: (error) => {
      console.error('OTP resend failed:', error);
    },
  });
};

// Manual token refresh hook
export const useRefreshToken = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { access_token: string; refresh_token: string },
    ApiError,
    void
  >({
    mutationFn: authApi.refreshToken,
    onSuccess: async (data) => {
      // Update stored tokens
      tokenStorage.setToken(data.access_token);
      if (data.refresh_token) {
        tokenStorage.setRefreshToken(data.refresh_token);
      }

      // Fetch fresh user data with updated permissions and roles
      try {
        const userData = tokenStorage.getUserData();
        if (userData && userData.id) {
          const freshUserData = await apiClient.get(`/users/${userData.id}`);
          tokenStorage.setUserData(freshUserData);
        }
      } catch (error) {
        console.error('Failed to refresh user data after manual token refresh:', error);
      }

      logAuthStage('token-refresh-success');
    },
    onError: (error) => {
      console.error('Token refresh failed:', error);
      // On refresh failure, clear all tokens, cache, and redirect to login
      queryClient.clear();
      tokenStorage.clearAll();
      authUtils.logout();
    },
  });
};

// Accept Invite mutation hook
export const useAcceptInvite = () => {
  return useMutation<AcceptInviteResponse, ApiError, AcceptInviteRequest>({
    mutationFn: authApi.acceptInvite,
    onSuccess: () => {
      logAuthStage('accept-invite-success');
    },
    onError: (error) => {
      console.error('Accept invite failed:', error);
    },
  });
};
